import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { RAG_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { reportApi, reportKeys } from '../api';

/**
 * Ngưỡng màu theo điểm `xác suất × ảnh hưởng`, thang 1–25.
 *
 * Cùng ngưỡng với cảnh báo rủi ro cao ở `APM-701` (score ≥ 15): một ô đỏ trên
 * ma trận và một thông báo "rủi ro cao" phải nói về cùng một tập rủi ro, nếu
 * không thì PM sẽ tin cái nào hiện ra sau.
 */
function cellColor(score: number, count: number): string {
  if (count === 0) return 'transparent';
  if (score >= 15) return RAG_VAR.red;
  if (score >= 8) return RAG_VAR.amber;
  return RAG_VAR.green;
}

/**
 * Ma trận rủi ro 5×5 — spec 16.4.
 *
 * Lưới CSS chứ không phải chart thư viện: heatmap 5×5 là một cái bảng, và một
 * cái bảng thật thì đọc được bằng bàn phím, chọn được bằng chuột, và không cần
 * 40KB JavaScript để vẽ 25 ô vuông.
 *
 * Trục dựng **đủ 25 ô** kể cả ô rỗng, và ảnh hưởng tăng dần từ dưới lên như mọi
 * ma trận rủi ro chuẩn — góc trên bên phải là chỗ nguy hiểm nhất.
 */
export function RiskMatrix({
  projectId,
  asOf,
}: {
  projectId: string;
  asOf?: string | undefined;
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: reportKeys.riskMatrix(projectId, asOf ?? ''),
    queryFn: () => reportApi.riskMatrix(projectId, asOf),
  });

  const cells = report.data?.cells ?? [];
  const cellAt = (probability: number, impact: number) =>
    cells.find((cell) => cell.probability === probability && cell.impact === impact);

  return (
    <ChartFrame
      title={t('reports.riskMatrixTitle')}
      help="riskMatrix"
      subtitle={t('reports.riskMatrixSubtitle', {
        count: report.data?.openCount ?? 0,
        exposure: report.data?.totalExposure ?? 0,
      })}
      asOf={asOf ? new Date(asOf) : undefined}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      empty={
        report.data && report.data.openCount === 0 && report.data.unscoredCount === 0
          ? { title: t('reports.riskMatrixEmpty'), description: t('reports.riskMatrixEmptyHelp') }
          : null
      }
      legend={[
        { label: t('rag.red'), color: RAG_VAR.red },
        { label: t('rag.amber'), color: RAG_VAR.amber },
        { label: t('rag.green'), color: RAG_VAR.green },
      ]}
    >
      <div className="flex flex-col gap-1">
        <div className="flex gap-1">
          <span className="flex items-center text-xs text-muted-foreground [writing-mode:vertical-rl]">
            {t('reports.riskImpact')}
          </span>

          <div className="grid flex-1 grid-cols-5 gap-1">
            {[5, 4, 3, 2, 1].map((impact) =>
              [1, 2, 3, 4, 5].map((probability) => {
                const cell = cellAt(probability, impact);
                const count = cell?.count ?? 0;

                return (
                  <div
                    key={`${probability}:${impact}`}
                    // `title` chứ không phải tooltip tự dựng: mã rủi ro trong ô
                    // là thứ người ta muốn chép ra, và trình duyệt làm việc đó
                    // sẵn rồi.
                    title={cell?.riskKeys.join(', ')}
                    className="flex h-10 items-center justify-center rounded-DEFAULT border border-border text-xs font-medium"
                    style={{
                      backgroundColor: cellColor(probability * impact, count),
                      color: count === 0 ? 'inherit' : 'var(--color-background)',
                    }}
                  >
                    {count > 0 ? count : ''}
                  </div>
                );
              }),
            )}
          </div>
        </div>

        <p className="pl-6 text-xs text-muted-foreground">{t('reports.riskProbability')}</p>

        {(report.data?.unscoredCount ?? 0) > 0 && (
          // Ma trận trống trơn cạnh mười rủi ro chưa chấm điểm là một màn hình
          // nói dối theo hướng lạc quan.
          <p className="text-xs text-rag-amber">
            {t('reports.riskUnscored', { count: report.data!.unscoredCount })}
          </p>
        )}
      </div>
    </ChartFrame>
  );
}
